import React, { useState } from 'react';
import { Plus, Edit3, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { pickLocalizedText } from '../../utils/localizedContent';


export default function AdminTables({ tables, setTables, lang, setMessage }) {
  const [showForm, setShowForm] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableForm, setTableForm] = useState({ table_number: '', capacity: 4, name: '', name_es: '', is_active: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tableNumber = parseInt(tableForm.table_number, 10);
    const capacity = parseInt(tableForm.capacity, 10);
    if (Number.isNaN(tableNumber) || Number.isNaN(capacity)) {
      setMessage(pickLocalizedText(lang, 'Numero de mesa y capacidad deben ser numeros', 'Table number and capacity must be numbers'));
      return;
    }
    const data = { ...tableForm, table_number: tableNumber, capacity };
    try {
      if (editingTable !== null) {
        await axios.put(`${API}/admin/tables/${editingTable}`, data);
        setMessage(pickLocalizedText(lang, 'Mesa actualizada', 'Table updated'));
      } else {
        await axios.post(`${API}/admin/tables`, data);
        setMessage(pickLocalizedText(lang, 'Mesa creada', 'Table created'));
      }
      setShowForm(false);
      setEditingTable(null);
      setTableForm({ table_number: '', capacity: 4, name: '', name_es: '', is_active: true });
      const { data: tb } = await axios.get(`${API}/admin/tables`);
      setTables(tb.tables || []);
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleDelete = async (tableNumber) => {
    if (!window.confirm(pickLocalizedText(lang, 'Eliminar mesa?', 'Delete table?'))) return;
    try {
      await axios.delete(`${API}/admin/tables/${tableNumber}`);
      setTables(prev => prev.filter(t => t.table_number !== tableNumber));
      setMessage(pickLocalizedText(lang, 'Mesa eliminada', 'Table deleted'));
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h2 className="text-white font-bold text-xl">{pickLocalizedText(lang, 'Gestion de Mesas', 'Tables Management')}</h2>
        <button onClick={() => { setShowForm(true); setEditingTable(null); setTableForm({ table_number: '', capacity: 4, name: '', name_es: '', is_active: true }); }} className="btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="new-table-btn">
          <Plus size={16} />{pickLocalizedText(lang, 'Nueva Mesa', 'New Table')}
        </button>
      </div>
      {showForm && (
        <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex justify-between mb-4">
            <h3 className="text-white font-bold">{editingTable ? pickLocalizedText(lang, 'Editar Mesa', 'Edit Table') : pickLocalizedText(lang, 'Nueva Mesa', 'New Table')}</h3>
            <button onClick={() => setShowForm(false)}><X size={18} className="text-[#A1A1AA]" /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input value={tableForm.table_number} onChange={e => setTableForm(f => ({ ...f, table_number: e.target.value }))} required type="number" placeholder={pickLocalizedText(lang, 'Numero de mesa', 'Table number')} className="bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#10B981]" />
            <input value={tableForm.capacity} onChange={e => setTableForm(f => ({ ...f, capacity: e.target.value }))} required type="number" placeholder={pickLocalizedText(lang, 'Capacidad', 'Capacity')} className="bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#10B981]" />
            <input value={tableForm.name} onChange={e => setTableForm(f => ({ ...f, name: e.target.value }))} placeholder="Name (EN)" className="bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#10B981]" />
            <input value={tableForm.name_es} onChange={e => setTableForm(f => ({ ...f, name_es: e.target.value }))} placeholder="Nombre (ES)" className="bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#10B981]" />
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={tableForm.is_active} onChange={e => setTableForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4" /><span className="text-[#A1A1AA] text-sm">{pickLocalizedText(lang, 'Activa', 'Active')}</span></label>
            <button type="submit" className="sm:col-span-2 btn-primary py-2.5 text-sm">{pickLocalizedText(lang, 'Guardar', 'Save')}</button>
          </form>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tables.map(t => (
          <div key={t.table_number} className="bg-[#12121A] border border-white/5 rounded-xl p-4" data-testid={`admin-table-${t.table_number}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-white font-bold">#{t.table_number} {t.name || ''}</p>
                <p className="text-[#A1A1AA] text-sm">{pickLocalizedText(lang, 'Capacidad:', 'Capacity:')} {t.capacity}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${t.is_active ? 'bg-[#10B981]/20 text-[#34D399]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>{t.is_active ? pickLocalizedText(lang, 'Activa', 'Active') : pickLocalizedText(lang, 'Inactiva', 'Inactive')}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setEditingTable(t.table_number); setTableForm({ table_number: t.table_number, capacity: t.capacity, name: t.name || '', name_es: t.name_es || '', is_active: t.is_active }); setShowForm(true); }} className="p-1.5 rounded-lg text-[#A1A1AA] hover:text-white hover:bg-white/5"><Edit3 size={14} /></button>
              <button onClick={() => handleDelete(t.table_number)} className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
